import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Copy,
  FileText,
  Filter,
  Gift,
  ExternalLink,
  IndianRupee,
  Megaphone,
  PackageCheck,
  Search,
  Send,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  X,
} from "lucide-react";
import api from "@/api";
import { useAuth } from "@/AuthContext";
import { useCompany } from "@/store";
import LogoMark from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const DATE_PRESETS = ["Today", "7D", "30D", "This Month", "FY", "Custom"];
const ACTIONS = [
  { key: "follow_up", title: "Follow Up Required", description: "Leads due today, overdue, or idle", action: "View Follow-ups", to: "/admin/leads", icon: Target, accent: "#4338CA" },
  { key: "quotation", title: "Quotation Required", description: "Qualified leads without a quotation", action: "Create Quotation", to: "/admin/leads", icon: FileText, accent: "#F5B83D" },
  { key: "approval", title: "Approval Required", description: "Items waiting for your review", action: "Review", to: "/admin/quotations", icon: ClipboardCheck, accent: "#0EA5E9" },
  { key: "job_assign", title: "Job Assign Required", description: "Accepted work without an assignee", action: "Assign Now", to: "/admin/jobs", icon: Users, accent: "#7C3AED" },
  { key: "job_completion", title: "Job Completion Required", description: "Scheduled work ready to complete", action: "Complete Jobs", to: "/admin/jobs", icon: PackageCheck, accent: "#E11D48" },
  { key: "settlement", title: "Settlement Required", description: "Completed work with billing outstanding", action: "Settle Now", to: "/admin/billing", icon: Wallet, accent: "#2A2380" },
];
const STATS = [
  { key: "sales", label: "Sales", prefix: "₹", to: "/admin/billing", icon: TrendingUp, financial: true, accent: "#4338CA" },
  { key: "purchase", label: "Purchase", prefix: "₹", to: "/admin/purchase", icon: PackageCheck, financial: true, accent: "#F5B83D" },
  { key: "expense", label: "Expense", prefix: "₹", to: "/admin/expenses", icon: Wallet, financial: true, accent: "#E11D48" },
  { key: "total_leads", label: "Total Lead", to: "/admin/leads", icon: Users, accent: "#0EA5E9" },
  { key: "total_billed", label: "Total Billed", prefix: "₹", to: "/admin/billing", icon: FileText, financial: true, accent: "#7C3AED" },
  { key: "jobs_completed", label: "Job Completed", to: "/admin/jobs", icon: Target, accent: "#A78BFA" },
  { key: "amount_due", label: "Amount Due", prefix: "₹", to: "/admin/billing", icon: IndianRupee, financial: true, accent: "#E11D48" },
  { key: "amount_received", label: "Amount Received", prefix: "₹", to: "/admin/billing", icon: Wallet, financial: true, accent: "#0EA5E9" },
];

function todayInIndia() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
}

function getRange(preset) {
  const today = new Date();
  const end = new Date(today);
  const start = new Date(today);

  if (preset === "Today") return { from_date: todayInIndia(), to_date: todayInIndia() };
  if (preset === "This Month") {
    start.setDate(1); start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === "FY") {
    const month = today.getMonth();
    start.setMonth(month >= 3 ? 3 : -9, 1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === "7D") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "30D") {
    start.setDate(start.getDate() - 29);
  } else {
    start.setDate(start.getDate() - 29);
  }

  return {
    from_date: start.toISOString().slice(0, 10),
    to_date: end.toISOString().slice(0, 10),
  };
}

function compactValue(value, prefix = "") {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const amount = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(Number(value));
  return `${prefix}${amount}`;
}

function formatDateLabel() {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

export default function DashboardHome({ onParamsChange, onFinancialAccess, alerts }) {
  const { user } = useAuth();
  const { company: selectedCompany } = useCompany();
  const navigate = useNavigate();

  const [home, setHome] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [customRange, setCustomRange] = useState(() => getRange("30D"));
  const [preset, setPreset] = useState("30D");
  const [filters, setFilters] = useState({ company: selectedCompany === "all" ? "" : selectedCompany, staff_id: "", service: "" });
  const [draftFilters, setDraftFilters] = useState({ company: selectedCompany === "all" ? "" : selectedCompany, staff_id: "", service: "" });
  const [filterOpen, setFilterOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedAction, setSelectedAction] = useState(null);
  const [paused, setPaused] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastRole, setBroadcastRole] = useState("all");
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [referral, setReferral] = useState(null);
  const [referralError, setReferralError] = useState(false);
  const [copied, setCopied] = useState(false);

  const params = useMemo(() => ({
    ...customRange,
    ...(filters.company ? { company: filters.company } : {}),
    ...(filters.staff_id ? { staff_id: filters.staff_id } : {}),
    ...(filters.service ? { service: filters.service } : {}),
  }), [customRange, filters]);

  useEffect(() => {
    onParamsChange?.(params);
  }, [onParamsChange, params]);

  const refreshHome = useCallback(() => {
    setLoadError(false);
    api.get("/dashboard/home", { params }).then((response) => {
      setHome(response.data);
      setSelectedAction((selected) => selected ? { ...selected, count: Number(response.data.counts?.[selected.key] ?? 0) } : selected);
      onFinancialAccess?.(response.data.financial_access !== false);
    }).catch(() => setLoadError(true));
    api.get("/dashboard/home/financial", { params }).then((response) => setFinancial(response.data)).catch((error) => {
      setFinancial(null);
      if (error.response?.status !== 403) {
        toast.error("Financial summary could not be loaded");
      }
    });
  }, [onFinancialAccess, params]);

  useEffect(() => {
    refreshHome();
    const timer = window.setInterval(refreshHome, 15000);
    return () => window.clearInterval(timer);
  }, [refreshHome]);

  useEffect(() => {
    api.get("/companies").then((response) => setCompanies(response.data || [])).catch(() => {});
    api.get("/users").then((response) => setStaff((response.data || []).filter((entry) => entry.active !== false))).catch(() => {});
    api.get("/products").then((response) => setServices([...new Set((response.data || []).map((item) => item.name || item.service).filter(Boolean))])).catch(() => {});
  }, []);

  const owner = user?.role === "super_admin" || Boolean(user?.is_workspace_owner);
  const isProductCompany = home?.industry_type === "product";
  const bannerSlides = useMemo(() => {
    if (home?.banners?.length) return home.banners;
    return [
      { title: `Welcome back, ${user?.name?.split(" ")[0] || "there"}`, subtitle: "Your workspace is ready for the next good day.", label: user?.tenant?.name || "Workspace" },
      { title: "Action focus", subtitle: "Track follow-ups, quotations, approvals, and completions in a single view.", label: "Today" },
      { title: "Performance keeps moving", subtitle: "Use filters to inspect the exact range for your team.", label: "Insights" },
    ];
  }, [home, user]);

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || paused || bannerSlides.length <= 1) return undefined;
    const timer = window.setInterval(() => setSlideIndex((index) => (index + 1) % bannerSlides.length), 4500);
    return () => window.clearInterval(timer);
  }, [bannerSlides.length, paused]);

  const activeSlide = bannerSlides[slideIndex % bannerSlides.length] || bannerSlides[0];
  const greetingHour = Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", hourCycle: "h23" }).format(new Date()));
  const greeting = greetingHour < 12 ? "Good Morning" : greetingHour < 17 ? "Good Afternoon" : "Good Evening";
  const roleLabel = user?.role ? user.role.replaceAll("_", " ") : "Member";
  const roleDisplay = user?.is_workspace_owner ? "Owner" : roleLabel;
  const anniversaryText = user?.birthday ? "Birthday today — wishing you a wonderful day!" : user?.joining_date ? "Work anniversary today — thank you for the years you have given us." : "";
  const displayStats = STATS.filter((stat) => !stat.financial || home?.financial_access !== false);
  const values = { ...(home?.stats || {}), ...(financial || {}) };

  const choosePreset = (value) => {
    setPreset(value);
    if (value !== "Custom") {
      setCustomRange(getRange(value));
    }
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFilterOpen(false);
  };

  const openSearch = () => {
    window.dispatchEvent(new Event("open-search"));
  };

  const sendBroadcast = async (event) => {
    event.preventDefault();
    if (!broadcastText.trim()) return;
    setBroadcastBusy(true);
    try {
      await api.post("/notifications/broadcast", { message: broadcastText.trim(), role: broadcastRole === "all" ? null : broadcastRole });
      toast.success("Announcement sent");
      setBroadcastOpen(false);
      setBroadcastText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send the announcement");
    } finally {
      setBroadcastBusy(false);
    }
  };

  const openReferrals = async () => {
    setReferralOpen(true);
    setReferralError(false);
    try {
      const response = await api.get("/referrals");
      setReferral(response.data || null);
    } catch (error) {
      setReferralError(true);
      toast.error(error.response?.data?.detail || "Referral details could not be loaded");
    }
  };

  const copyReferral = async () => {
    if (!referral?.link) return;
    try {
      await navigator.clipboard.writeText(referral.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed, please try again");
    }
  };

  return (
    <div className="min-w-0 flex flex-col gap-5" data-testid="dashboard-home">
      <header className="order-1 sticky top-[3.5rem] z-20 -mx-3 flex items-center justify-between gap-2 border-b border-border/70 bg-background/80 px-3 py-2 shadow-sm backdrop-blur-xl sm:-mx-6 sm:px-6" data-testid="dashboard-top-bar">
        <Link to="/admin" className="flex shrink-0 items-center gap-2" aria-label="Go to dashboard">
          {user?.tenant?.logo ? <img src={user.tenant.logo} alt="" className="h-9 w-9 rounded-md object-contain" /> : <LogoMark size={34} />}
          <span className="hidden max-w-36 truncate text-sm font-semibold sm:block">{user?.tenant?.name || "Dashboard"}</span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            title={owner ? "Send announcement" : "Broadcasts"}
            aria-label="Broadcast"
            data-testid="dashboard-broadcast-button"
            onClick={() => (owner ? setBroadcastOpen(true) : navigate("/admin/notifications"))}
            className="relative grid h-10 w-10 place-items-center rounded-xl border border-border/60 bg-card/70 text-muted-foreground transition duration-200 hover:-translate-y-0.5 hover:bg-accent active:scale-95"
          >
            <Megaphone size={18} />
            {home?.unread_broadcasts ? <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-danger" /> : null}
          </button>

          <div title="Notifications">
            <NotificationBell />
          </div>

          <button type="button" onClick={openReferrals} title="Refer & Earn" aria-label="Refer and earn" data-testid="dashboard-referral-button" className="grid h-10 w-10 place-items-center rounded-xl border border-border/60 bg-card/70 text-muted-foreground transition duration-200 hover:-translate-y-0.5 hover:bg-accent active:scale-95">
            <Gift size={18} />
          </button>

          <button type="button" onClick={openSearch} title="Search (Ctrl/Cmd+K)" aria-label="Search" data-testid="dashboard-search-button" className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-border/60 bg-card/70 px-3 text-muted-foreground transition duration-200 hover:-translate-y-0.5 hover:bg-accent active:scale-95">
            <Search size={18} />
            <span className="hidden text-xs font-medium sm:inline">Search</span>
          </button>

          <button type="button" onClick={() => { setDraftFilters(filters); setFilterOpen(true); }} title="Filter dashboard" aria-label="Filter dashboard" data-testid="dashboard-filter-button" className="inline-flex h-10 items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 text-xs font-semibold text-primary transition hover:bg-primary/10 active:scale-[0.98]">
            <Filter size={15} />
            <span className="hidden sm:inline">{preset}</span>
          </button>
        </div>
      </header>

      <section aria-labelledby="dashboard-greeting" className="order-2 space-y-2" data-testid="dashboard-greeting-section">
        <h1 id="dashboard-greeting" className="font-heading text-2xl font-bold sm:text-3xl">
          {greeting}, <span className="text-primary">{user?.name || "there"}</span> <span aria-hidden="true">👋</span>
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {[formatDateLabel(), roleDisplay, user?.tenant?.name || "Your workspace"].map((label) => <span key={label} className="rounded-full border border-border/70 bg-card/70 px-3 py-1.5">{label}</span>)}
        </div>
        {home ? <p className="text-sm text-muted-foreground">You have {home.counts?.follow_up || 0} follow-ups and {(home.counts?.job_assign || 0) + (home.counts?.job_completion || 0)} jobs to review today.</p> : null}
        {anniversaryText ? <p className="text-sm font-medium text-primary">{anniversaryText}</p> : null}
      </section>

      <section className="relative isolate flex min-h-[172px] flex-col justify-center overflow-hidden rounded-3xl bg-[#2A2380] px-5 py-6 text-white shadow-xl shadow-[#2A2380]/20 sm:min-h-[224px] sm:px-8 sm:py-7" data-testid="dashboard-banner" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}>
        <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full border-[28px] border-white/[0.06]" />
        <div aria-hidden="true" className="pointer-events-none absolute right-32 top-8 grid grid-cols-7 gap-2 opacity-20 sm:right-56">
          {Array.from({ length: 35 }, (_, index) => <span key={index} className="h-1.5 w-1.5 rounded-full bg-[#F5B83D]" />)}
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute bottom-[-72px] right-[28%] h-40 w-40 rounded-full border border-[#F5B83D]/15" />
        <div className="relative flex max-w-3xl flex-col justify-center gap-2 pr-2 sm:pr-24" key={`${activeSlide?.title}-${slideIndex}`}>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
            <Sparkles size={14} />
            {activeSlide?.label || "Workspace update"}
          </div>
          <h2 className="font-heading text-lg font-bold sm:text-2xl">{activeSlide?.title || "Welcome back"}</h2>
          <p className="max-w-2xl text-sm text-white/80">{activeSlide?.subtitle || "Your key priorities are ready to review."}</p>
          <Link to={activeSlide?.link || "/admin/leads"} className="mt-2 inline-flex min-h-10 w-fit items-center gap-2 rounded-xl bg-[#F5B83D] px-4 text-sm font-bold text-[#211B62] shadow-md transition hover:-translate-y-0.5 hover:shadow-lg">{activeSlide?.button_text || "Review priorities"}<ArrowRight size={15} /></Link>
        </div>

        <div className="absolute bottom-3 right-4 flex items-center gap-2">
          <button type="button" aria-label="Previous banner" onClick={() => setSlideIndex((index) => (index - 1 + bannerSlides.length) % bannerSlides.length)} className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20">
            <ChevronLeft size={16} />
          </button>
          {bannerSlides.map((slide, index) => (
            <button key={`${slide.title}-${index}`} type="button" aria-label={`Show banner ${index + 1}`} aria-current={index === slideIndex} onClick={() => setSlideIndex(index)} className={`h-2 rounded-full transition-all ${index === slideIndex ? "w-5 bg-[#F5B83D]" : "w-2 bg-white/40"}`} />
          ))}
          <button type="button" aria-label="Next banner" onClick={() => setSlideIndex((index) => (index + 1) % bannerSlides.length)} className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20">
            <ChevronRight size={16} />
          </button>
        </div>
      </section>

      {alerts && (alerts.pending_quotes > 0 || alerts.low_stock?.length > 0 || alerts.pending_leaves > 0) ? (
        <section aria-label="Alerts" className="flex flex-wrap items-center gap-2" data-testid="dashboard-alerts-strip">
          {alerts.pending_quotes > 0 ? <Link to="/admin/quotations" className="inline-flex min-h-9 items-center gap-2 rounded-full border border-[#F5B83D]/35 bg-[#F5B83D]/10 px-3 text-xs font-medium transition hover:bg-[#F5B83D]/20"><span className="h-2 w-2 rounded-full bg-[#F5B83D]" /><b>{alerts.pending_quotes}</b> quotations awaiting approval<ArrowRight size={13} /></Link> : null}
          {alerts.low_stock?.length > 0 ? <Link to="/admin/stock" className="inline-flex min-h-9 items-center gap-2 rounded-full border border-[#E11D48]/30 bg-[#E11D48]/10 px-3 text-xs font-medium transition hover:bg-[#E11D48]/15"><span className="h-2 w-2 rounded-full bg-[#E11D48]" /><b>{alerts.low_stock.length}</b> items below reorder threshold<ArrowRight size={13} /></Link> : null}
          {alerts.pending_leaves > 0 ? <Link to="/admin/leaves" className="inline-flex min-h-9 items-center gap-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 px-3 text-xs font-medium transition hover:bg-[#0EA5E9]/15"><span className="h-2 w-2 rounded-full bg-[#0EA5E9]" /><b>{alerts.pending_leaves}</b> leave requests pending<ArrowRight size={13} /></Link> : null}
        </section>
      ) : null}

      <section aria-labelledby="action-required-heading" data-testid="dashboard-actions-section">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 id="action-required-heading" className="font-heading text-lg font-semibold">Action Required</h2>
            <p className="text-xs text-muted-foreground">Tasks waiting for you</p>
          </div>
          {home ? <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-bold tabular-nums text-primary">{Object.values(home.counts || {}).reduce((total, count) => total + Number(count || 0), 0)} pending</span> : null}
        </div>

        {loadError ? <RetryState onRetry={refreshHome} label="Action counts could not be loaded" /> : !home ? (
          <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-3">
            {ACTIONS.map((item) => (
              <div key={item.key} className="h-36 min-w-[84%] snap-start animate-pulse rounded-2xl bg-muted sm:min-w-0" />
            ))}
          </div>
        ) : (
          <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-3">
            {ACTIONS.map(({ key, title, description, action, to, icon: Icon, accent: baseAccent }) => {
              const count = Number(home?.counts?.[key] ?? 0);
              const titleText = isProductCompany && (key === "job_assign" || key === "job_completion")
                ? { job_assign: "Order Dispatch Required", job_completion: "Delivery Completion Required" }[key]
                : title;
              const accent = count ? baseAccent : "#0EA5E9";
              const ActionIcon = count ? Icon : Check;
              return (
                <article key={key} style={{ "--action-accent": accent, "--action-tint": `${accent}14` }} className="group relative flex min-h-52 min-w-[84%] snap-start flex-col justify-between overflow-hidden rounded-3xl border border-border/50 border-t-[3px] border-t-[color:var(--action-accent)] bg-[color:var(--action-tint)] p-4 shadow-sm transition duration-200 hover:-translate-y-1 hover:shadow-xl sm:min-w-0 sm:p-5" data-testid={`action-card-${key}`}>
                  <div className="flex items-start gap-3">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[color:var(--action-accent)]/15 text-[color:var(--action-accent)]">
                      <ActionIcon size={21} />
                    </span>
                  </div>
                  <div>
                    <div className="font-mono text-[42px] font-extrabold leading-none tabular-nums text-foreground sm:text-5xl" data-testid={`action-count-${key}`}>{count}</div>
                    <h3 className="mt-2 text-sm font-bold leading-snug">{titleText}</h3>
                    <p className="mt-1 min-h-8 text-xs leading-relaxed text-muted-foreground">{count ? description : "All clear"}</p>
                  </div>
                  <button type="button" onClick={() => setSelectedAction({ key, title: titleText, description, action, to, count, icon: ActionIcon })} aria-label={`${count ? action : "View"}: ${titleText}`} className="mt-3 inline-flex min-h-10 w-full items-center justify-between gap-2 rounded-xl bg-[color:var(--action-accent)] px-3 text-left text-xs font-bold text-white transition hover:brightness-110" data-testid={`action-link-${key}`}>
                    <span>{count ? action : "All clear"}</span>
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section aria-labelledby="dashboard-stats-heading" data-testid="dashboard-stats-section">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 id="dashboard-stats-heading" className="font-heading text-lg font-semibold">Business Overview</h2>
            <p className="text-xs text-muted-foreground">{DATE_PRESETS.includes(preset) ? <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 font-medium text-primary">{preset}</span> : "Selected period"}</p>
          </div>
        </div>

        {!home ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[0, 1, 2, 3].map((item) => <div key={item} className="h-28 animate-pulse rounded-2xl bg-primary/10" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {displayStats.map(({ key, label, prefix, icon: Icon, accent }, index) => (
              <Link key={key} to={STATS.find((item) => item.key === key)?.to || "/admin"} data-testid={`dashboard-stat-${key}`} style={{ "--stat-accent": accent, "--stat-tint": `${accent}12` }} className="group min-w-0 overflow-hidden rounded-2xl border border-border/60 bg-[color:var(--stat-tint)] p-3 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg sm:p-4">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[color:var(--stat-accent)]/15 text-[color:var(--stat-accent)]"><Icon size={17} /></span>
                  <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
                </div>
                <div className="mt-3 truncate font-mono text-xl font-bold sm:text-2xl" data-testid={`dashboard-stat-value-${key}`}>
                  {compactValue(values[key], prefix)}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-muted-foreground">Selected period</span>
                  <span className="h-5 w-14" aria-hidden="true"><MiniSpark value={Number(values[key]) || index + 1} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {selectedAction ? (
        <div className="fixed inset-0 z-[70] flex justify-end bg-background backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedAction(null); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="dashboard-work-window-title" className="flex h-full w-full max-w-md flex-col border-l border-border/70 bg-background/95 p-5 shadow-layered backdrop-blur-xl animate-in slide-in-from-right duration-200 sm:p-6" data-testid={`work-window-${selectedAction.key}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><selectedAction.icon size={19} /></span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Work window</p>
                  <h2 id="dashboard-work-window-title" className="mt-1 font-heading text-lg font-bold">{selectedAction.title}</h2>
                </div>
              </div>
              <button type="button" onClick={() => setSelectedAction(null)} aria-label="Close work window" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-border bg-card text-muted-foreground transition hover:bg-accent"><X size={18} /></button>
            </div>
            <div className="mt-6 rounded-2xl border border-border/70 bg-card/80 p-4 shadow-soft">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Items to review</div>
              <div className="mt-2 font-mono text-4xl font-bold tabular-nums">{selectedAction.count}</div>
              <p className="mt-2 text-sm text-muted-foreground">{selectedAction.count ? selectedAction.description : "Nothing is waiting in this queue right now."}</p>
            </div>
            <div className="mt-6 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Quick actions</h3>
              <Link to={selectedAction.to} onClick={() => setSelectedAction(null)} className="flex min-h-12 items-center justify-between rounded-xl border border-border/70 bg-card px-4 text-sm font-medium transition hover:border-primary/30 hover:bg-primary/[0.04]">
                <span>{selectedAction.action}</span><ArrowRight size={16} className="text-primary" />
              </Link>
              {selectedAction.key === "follow_up" || selectedAction.key === "quotation" ? <Link to="/admin/quotations" onClick={() => setSelectedAction(null)} className="flex min-h-12 items-center justify-between rounded-xl border border-border/70 bg-card px-4 text-sm font-medium transition hover:border-primary/30 hover:bg-primary/[0.04]"><span>Review quotations</span><ArrowRight size={16} className="text-primary" /></Link> : null}
              {selectedAction.key === "job_assign" || selectedAction.key === "job_completion" ? <Link to="/admin/calendar" onClick={() => setSelectedAction(null)} className="flex min-h-12 items-center justify-between rounded-xl border border-border/70 bg-card px-4 text-sm font-medium transition hover:border-primary/30 hover:bg-primary/[0.04]"><span>Open schedule</span><ArrowRight size={16} className="text-primary" /></Link> : null}
            </div>
            <div className="mt-auto pt-6">
              <Link to={selectedAction.to} onClick={() => setSelectedAction(null)} className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-soft transition hover:brightness-105">
                Open in full page <ExternalLink size={15} />
              </Link>
            </div>
          </section>
        </div>
      ) : null}

      {filterOpen ? (
        <div className="fixed inset-0 z-50 flex items-end bg-overlay sm:items-center sm:justify-center" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setFilterOpen(false); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="dashboard-filter-title" className="max-h-[88vh] w-full overflow-y-auto rounded-t-xl border bg-background p-5 shadow-xl sm:max-w-lg sm:rounded-xl" data-testid="dashboard-filter-sheet">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="dashboard-filter-title" className="font-heading text-lg font-semibold">Dashboard filters</h2>
              <button type="button" aria-label="Close filters" onClick={() => setFilterOpen(false)} className="grid h-9 w-9 place-items-center rounded-md hover:bg-accent"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <fieldset>
                <legend className="mb-2 text-xs font-semibold text-muted-foreground">Date range</legend>
                <div className="grid grid-cols-3 gap-2">
                  {DATE_PRESETS.map((item) => (
                    <button key={item} type="button" onClick={() => choosePreset(item)} data-testid={`dashboard-date-${item.toLowerCase().replaceAll(" ", "-")}`} className={`min-h-10 rounded-md border px-2 text-xs font-medium ${preset === item ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
                      {item}
                    </button>
                  ))}
                </div>
              </fieldset>

              {preset === "Custom" ? (
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs text-muted-foreground">
                    From
                    <input type="date" value={customRange.from_date || ""} onChange={(event) => setCustomRange((current) => ({ ...current, from_date: event.target.value }))} className="mt-1 h-10 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
                  </label>
                  <label className="text-xs text-muted-foreground">
                    To
                    <input type="date" value={customRange.to_date || ""} onChange={(event) => setCustomRange((current) => ({ ...current, to_date: event.target.value }))} className="mt-1 h-10 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
                  </label>
                </div>
              ) : null}

              <label className="block text-xs font-medium text-muted-foreground">
                Branch / company
                <select value={draftFilters.company} onChange={(event) => setDraftFilters((current) => ({ ...current, company: event.target.value }))} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground">
                  <option value="">All companies</option>
                  {companies.map((company) => <option key={company.id || company.name} value={company.id || company.name}>{company.name || company.id}</option>)}
                </select>
              </label>

              {owner ? (
                <label className="block text-xs font-medium text-muted-foreground">
                  Staff
                  <select value={draftFilters.staff_id} onChange={(event) => setDraftFilters((current) => ({ ...current, staff_id: event.target.value }))} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground">
                    <option value="">All staff</option>
                    {staff.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                  </select>
                </label>
              ) : null}

              <label className="block text-xs font-medium text-muted-foreground">
                Service
                <select value={draftFilters.service} onChange={(event) => setDraftFilters((current) => ({ ...current, service: event.target.value }))} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground">
                  <option value="">All services</option>
                  {services.map((service) => <option key={service} value={service}>{service}</option>)}
                </select>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => { const reset = { company: selectedCompany === "all" ? "" : selectedCompany, staff_id: "", service: "" }; setDraftFilters(reset); setFilters(reset); setPreset("30D"); setCustomRange(getRange("30D")); setFilterOpen(false); }} className="h-10 rounded-md border px-4 text-sm">Reset</button>
              <button type="button" onClick={applyFilters} data-testid="apply-dashboard-filters" className="h-10 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground">Apply filters</button>
            </div>
          </section>
        </div>
      ) : null}

      {broadcastOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-overlay p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setBroadcastOpen(false); }}>
          <form onSubmit={sendBroadcast} role="dialog" aria-modal="true" aria-labelledby="broadcast-title" className="w-full max-w-lg rounded-xl border bg-background p-5 shadow-xl" data-testid="broadcast-dialog">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="broadcast-title" className="font-heading text-lg font-semibold">New announcement</h2>
              <button type="button" aria-label="Close announcement" onClick={() => setBroadcastOpen(false)} className="grid h-9 w-9 place-items-center rounded-md hover:bg-accent"><X size={18} /></button>
            </div>

            <label className="block text-xs font-medium text-muted-foreground">
              Audience
              <select value={broadcastRole} onChange={(event) => setBroadcastRole(event.target.value)} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground">
                <option value="all">Everyone in this workspace</option>
                {['admin', 'telecaller', 'accountant', 'team_leader'].map((role) => <option key={role} value={role}>{role.replaceAll("_", " ")}</option>)}
              </select>
            </label>

            <label className="mt-4 block text-xs font-medium text-muted-foreground">
              Message
              <textarea value={broadcastText} onChange={(event) => setBroadcastText(event.target.value)} maxLength={1000} required rows={4} placeholder="Write an announcement for your team" className="mt-1 w-full resize-y rounded-md border bg-background p-3 text-sm text-foreground" />
            </label>

            <div className="mt-4 flex justify-end">
              <button type="submit" disabled={broadcastBusy || !broadcastText.trim()} data-testid="send-broadcast-button" className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                <Send size={15} />
                {broadcastBusy ? "Sending…" : "Send announcement"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {referralOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-overlay p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setReferralOpen(false); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="referral-title" className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-background p-5 shadow-xl" data-testid="referral-dialog">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="referral-title" className="font-heading text-lg font-semibold">Refer & Earn</h2>
              <button type="button" aria-label="Close referrals" onClick={() => setReferralOpen(false)} className="grid h-9 w-9 place-items-center rounded-md hover:bg-accent"><X size={18} /></button>
            </div>

            {referralError ? <RetryState onRetry={openReferrals} label="Referral details could not be loaded" /> : !referral ? (
              <div className="h-28 animate-pulse rounded-lg bg-muted" />
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Invite a business to {user?.tenant?.name || "your workspace"}.</p>
                <div className="mt-3 rounded-lg border bg-muted/30 p-3">
                  <div className="text-[11px] font-semibold uppercase text-muted-foreground">Referral code</div>
                  <div className="mt-1 font-mono text-lg font-bold">{referral.code || "—"}</div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={copyReferral} className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md border text-sm font-medium hover:bg-accent">
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                    {copied ? "Copied" : "Copy link"}
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`Join ${user?.tenant?.name || "us"}: ${referral.link || ""}`)}`} target="_blank" rel="noreferrer" className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-success text-sm font-semibold text-inverse hover:bg-success/90">
                    Share on WhatsApp
                  </a>
                </div>
                <div className="mt-5 space-y-3">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="text-sm font-semibold">Referrals</h3>
                    <span className="text-xs text-muted-foreground">{referral.referrals?.length || 0} total</span>
                  </div>
                  {(referral.referrals || []).slice(0, 4).map((entry) => (
                    <div key={entry.id || entry.name} className="flex items-center justify-between rounded-md border p-2 text-sm">
                      <span>{entry.name}</span>
                      <span className="text-muted-foreground">{entry.status || "Pending"}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

function MiniSpark({ value }) {
  const bars = [0.35, 0.52, 0.42, 0.7, 0.58, 0.83, 0.68].map((part, index) => Math.max(3, Math.min(18, Math.round(part * 18 * (1 + ((Math.abs(value) + index) % 3) / 8)))));
  return (
    <span className="flex h-full items-end justify-between gap-0.5">
      {bars.map((height, index) => <i key={index} className="w-1 rounded-sm bg-primary/45" style={{ height }} />)}
    </span>
  );
}

function RetryState({ onRetry, label }) {
  return (
    <div className="flex min-h-24 items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3">
      <p className="text-sm text-muted-foreground">{label}</p>
      <button type="button" onClick={onRetry} className="h-9 shrink-0 rounded-md border px-3 text-sm font-medium hover:bg-accent">Retry</button>
    </div>
  );
}
